import { parseLength } from '../core/units';
import { VanState, VanTemplate, templateById } from '../vans/vans';
import { CustomFurnitureData, FurnitureTemplate, FURNITURE_TEMPLATES } from '../furniture/furniture';
import { formatLength } from '../core/units';

/** Rechtes Panel: Van-Vorlage, Ebenen, Möbel stellen (Katalog + eigene), Farben, Info. */
export interface PanelsCallbacks {
  onVanChange(state: VanState): void;
  onColorPick(color: string): void;
  onPlaceFurniture(tpl: FurnitureTemplate | CustomFurnitureData): void;
  /** Ausgewählte Gruppe als eigenes Möbel speichern; liefert Rückmeldung für die Hinweiszeile. */
  onSaveSelectionAsFurniture(name: string): { ok: boolean; message: string };
  /** Eigenes Möbel aus der Bibliothek entfernen. */
  onDeleteCustomFurniture(id: string): void;
  /** Ebene ein-/ausblenden. */
  onToggleLayerVisible(target: LayerTarget): void;
  /** Ebene in der Szene wählen (Klick auf den Namen) — macht sie zum aktiven Objekt. */
  onSelectLayer(target: LayerTarget): void;
  /** Alle Ebenen wieder einblenden. */
  onShowAllLayers(): void;
}

/** Ziel einer Ebenen-Aktion: lose Modellebene oder eine oberste Gruppe. */
export type LayerTarget = { kind: 'loose' } | { kind: 'group'; id: number };

/** Eine Zeile im Ebenen-Panel. */
export interface LayerInfo {
  key: string;
  groupId: number | null;
  name: string;
  visible: boolean;
  opacity: number;
}

const EYE_ON =
  '<svg viewBox="0 0 24 24" width="15" height="15"><path d="M2 12s3.5-6.2 10-6.2S22 12 22 12s-3.5 6.2-10 6.2S2 12 2 12z" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="12" cy="12" r="2.6" fill="currentColor"/></svg>';
const EYE_OFF =
  '<svg viewBox="0 0 24 24" width="15" height="15"><path d="M4 4l16 16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M10 5.2c.65-.13 1.32-.2 2-.2 6.5 0 10 7 10 7a17.6 17.6 0 0 1-2.9 3.6M6.3 6.3A16.5 16.5 0 0 0 2 12s3.5 7 10 7c1.7 0 3.2-.45 4.5-1.1" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>';

/** Eine DOM-Zeile der Ebenenliste. */
interface LayerRow {
  root: HTMLElement;
  eye: HTMLButtonElement;
  name: HTMLElement;
  pct: HTMLElement;
  target: LayerTarget;
}

export const CAMPER_COLORS = [
  '#ffffff', '#d9cbb3', '#b78e5e', '#7a5c3e', '#4e4a45',
  '#8fa9b8', '#5b7f95', '#3f6f5f', '#9dbb7a', '#d97b4f',
  '#c14f4f', '#e0c14f'
];

export class Panels {
  el: HTMLElement;
  private state: VanState;
  private cb: PanelsCallbacks;
  private lenInput: HTMLInputElement;
  private widInput: HTMLInputElement;
  private heiInput: HTMLInputElement;
  private sel: HTMLSelectElement;
  private vanVisCb: HTMLInputElement;
  private archCb: HTMLInputElement;
  private infoEl: HTMLElement;
  private colorSwatches = new Map<string, HTMLButtonElement>();
  private builtinButtons = new Map<string, HTMLButtonElement>();
  private customButtons = new Map<string, HTMLButtonElement>();
  private customGrid: HTMLElement;
  private saveInput: HTMLInputElement;
  private furnitureMsg: HTMLElement;
  private layersList: HTMLElement;
  private layersEmpty: HTMLElement;
  private showAllBtn: HTMLButtonElement;
  private layerRows = new Map<string, LayerRow>();

  constructor(root: HTMLElement, vans: VanTemplate[], state: VanState, cb: PanelsCallbacks, currentColor: string) {
    this.state = { ...state };
    this.cb = cb;
    this.el = document.createElement('div');
    this.el.className = 'panels';

    // — Van —
    const vanBox = section('Camper');
    this.sel = document.createElement('select');
    this.sel.className = 'van-select';
    for (const v of vans) {
      const opt = document.createElement('option');
      opt.value = v.id;
      opt.textContent = v.name;
      if (v.id === state.templateId) opt.selected = true;
      this.sel.appendChild(opt);
    }
    this.sel.onchange = () => {
      const t = templateById(this.sel.value);
      this.state = { ...this.state, templateId: t.id, length: t.length, width: t.width, height: t.height };
      this.syncInputs();
      this.cb.onVanChange({ ...this.state });
    };
    vanBox.appendChild(this.sel);

    const dims = document.createElement('div');
    dims.className = 'dim-grid';
    dims.innerHTML = '<label>Länge</label><label>Breite</label><label>Höhe</label>';
    this.lenInput = dimInput();
    this.widInput = dimInput();
    this.heiInput = dimInput();
    dims.append(this.lenInput, this.widInput, this.heiInput);
    vanBox.appendChild(dims);
    const note = document.createElement('p');
    note.className = 'panel-note';
    note.textContent = 'Innenmaße in cm · Näherungswerte, bitte am Fahrzeug prüfen';
    vanBox.appendChild(note);

    const visRow = checkboxRow('Van anzeigen', state.visible, (v) => {
      this.state = { ...this.state, visible: v };
      this.cb.onVanChange({ ...this.state });
    });
    this.vanVisCb = visRow.querySelector('input')!;
    const archRow = checkboxRow('Radkästen anzeigen', state.arches, (v) => {
      this.state = { ...this.state, arches: v };
      this.cb.onVanChange({ ...this.state });
    });
    this.archCb = archRow.querySelector('input')!;
    vanBox.append(visRow, archRow);

    for (const [input, key] of [
      [this.lenInput, 'length'], [this.widInput, 'width'], [this.heiInput, 'height']
    ] as const) {
      input.onchange = () => {
        // Felder sind in cm; Einheiten („4,7m“, „4700mm“) werden verstanden
        const m = parseLength(input.value, 'cm');
        if (m !== null && m >= 0.5 && m <= 20) {
          this.state = { ...this.state, [key]: m, templateId: 'custom' };
          this.sel.value = 'custom';
          this.cb.onVanChange({ ...this.state });
        }
        this.syncInputs();
      };
    }

    // — Ebenen (Sichtbarkeit & Deckkraft) —
    const layerSection = section('Ebenen');
    this.layersList = document.createElement('div');
    this.layersList.className = 'layer-list';
    this.layersEmpty = document.createElement('p');
    this.layersEmpty.className = 'layer-empty';
    this.layersEmpty.textContent = 'Noch keine Objekte — Gruppen erscheinen hier (Strg+G gruppiert).';
    this.showAllBtn = document.createElement('button');
    this.showAllBtn.type = 'button';
    this.showAllBtn.className = 'layer-show-all';
    this.showAllBtn.textContent = 'Alle einblenden';
    this.showAllBtn.title = 'Alle ausgeblendeten Ebenen wieder einblenden';
    this.showAllBtn.onclick = () => this.cb.onShowAllLayers();
    layerSection.append(this.layersList, this.layersEmpty, this.showAllBtn);

    // — Möbel stellen —
    const furnitureBox = section('Möbel stellen');
    const furnitureGrid = document.createElement('div');
    furnitureGrid.className = 'furniture-grid';
    for (const tpl of FURNITURE_TEMPLATES) {
      const b = this.furnitureButton(tpl);
      this.builtinButtons.set(tpl.id, b);
      furnitureGrid.appendChild(b);
    }
    furnitureBox.appendChild(furnitureGrid);

    // Eigenes gezeichnetes Möbel speichern
    const saveRow = document.createElement('div');
    saveRow.className = 'furniture-save';
    this.saveInput = document.createElement('input');
    this.saveInput.type = 'text';
    this.saveInput.className = 'dim-input';
    this.saveInput.placeholder = 'Eigenes Möbel…';
    this.saveInput.title = 'Name für das gespeicherte Möbel (leer = Gruppenname)';
    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'furniture-save-btn';
    saveBtn.textContent = 'Auswahl speichern';
    saveBtn.title = 'Ausgewählte Gruppe als Möbel in die Bibliothek aufnehmen';
    saveBtn.onclick = () => this.saveSelection();
    saveRow.append(this.saveInput, saveBtn);
    furnitureBox.appendChild(saveRow);
    this.saveInput.onkeydown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.saveSelection();
      }
    };

    const subLabel = document.createElement('p');
    subLabel.className = 'furniture-sub';
    subLabel.textContent = 'Eigene Möbel';
    furnitureBox.appendChild(subLabel);
    this.customGrid = document.createElement('div');
    this.customGrid.className = 'furniture-grid';
    furnitureBox.appendChild(this.customGrid);
    this.furnitureMsg = document.createElement('p');
    this.furnitureMsg.className = 'furniture-msg';
    furnitureBox.appendChild(this.furnitureMsg);

    const furnitureHint = document.createElement('p');
    furnitureHint.className = 'panel-note';
    furnitureHint.textContent =
      'Maße in cm · Auswählen, dann in die Szene klicken — jedes Möbel ist eine verschiebbare Gruppe · ←/→ dreht um 90° · Eigene: Gruppe wählen, oben benennen und speichern';
    furnitureBox.appendChild(furnitureHint);

    // — Farben —
    const colorBox = section('Farben');
    const swatchRow = document.createElement('div');
    swatchRow.className = 'swatches';
    for (const c of CAMPER_COLORS) {
      const b = document.createElement('button');
      b.className = 'swatch';
      b.style.background = c;
      b.title = c;
      b.onclick = () => this.pickColor(c);
      this.colorSwatches.set(c, b);
      swatchRow.appendChild(b);
    }
    colorBox.appendChild(swatchRow);
    const colorHint = document.createElement('p');
    colorHint.className = 'panel-note';
    colorHint.textContent = 'Farbe wählen, dann mit Werkzeug „Farbe“ (B) Flächen anklicken';
    colorBox.appendChild(colorHint);

    // — Info —
    const infoBox = section('Info');
    this.infoEl = document.createElement('div');
    this.infoEl.className = 'info';
    infoBox.appendChild(this.infoEl);

    this.el.append(vanBox, layerSection, furnitureBox, colorBox, infoBox);
    root.appendChild(this.el);
    this.syncInputs();
    this.pickColor(currentColor, false);
  }

  private pickColor(c: string, notify = true): void {
    for (const [color, btn] of this.colorSwatches) {
      btn.classList.toggle('active', color === c);
    }
    if (notify) this.cb.onColorPick(c);
  }

  /** Hebt den zuletzt gewählten Möbelknopf hervor (null = keiner aktiv). */
  setActiveFurniture(id: string | null): void {
    for (const map of [this.builtinButtons, this.customButtons]) {
      for (const [tid, btn] of map) {
        btn.classList.toggle('active', tid === id);
      }
    }
  }

  /** Baut die Ebenenliste neu auf — bestehende Zeilen werden in-place aktualisiert.
   *  Die Deckkraft selbst ist eine Objekt-Eigenschaft und wird im Auswahl-Panel
   *  bearbeitet, sobald das Objekt aktiv (gewählt) ist; hier steht nur der Wert. */
  refreshLayers(list: LayerInfo[]): void {
    const keys = new Set(list.map((l) => l.key));
    for (const [key, row] of this.layerRows) {
      if (!keys.has(key)) {
        row.root.remove();
        this.layerRows.delete(key);
      }
    }
    for (const info of list) {
      let row = this.layerRows.get(info.key);
      if (!row) {
        row = this.createLayerRow(info);
        this.layerRows.set(info.key, row);
        this.layersList.appendChild(row.root);
      }
      row.name.textContent = info.name;
      row.eye.innerHTML = info.visible ? EYE_ON : EYE_OFF;
      row.eye.title = info.visible ? 'Ausblenden' : 'Einblenden';
      row.root.classList.toggle('layer-hidden', !info.visible);
      const pct = Math.round(info.opacity * 100);
      row.pct.textContent = pct < 100 ? `${pct}%` : '';
      row.pct.title = pct < 100 ? `Deckkraft ${pct} %` : '';
    }
    this.showAllBtn.disabled = !list.some((l) => !l.visible);
    this.layersEmpty.style.display = list.length === 0 ? '' : 'none';
  }

  private createLayerRow(info: LayerInfo): LayerRow {
    const target: LayerTarget = info.groupId === null ? { kind: 'loose' } : { kind: 'group', id: info.groupId };
    const root = document.createElement('div');
    root.className = 'layer-row';

    const eye = document.createElement('button');
    eye.type = 'button';
    eye.className = 'layer-eye';
    eye.innerHTML = info.visible ? EYE_ON : EYE_OFF;
    eye.onclick = () => this.cb.onToggleLayerVisible(target);

    const name = document.createElement('span');
    name.className = 'layer-name';
    name.textContent = info.name;
    name.title = 'Klicken: Objekt in der Szene wählen';
    name.onclick = () => this.cb.onSelectLayer(target);

    const pct = document.createElement('span');
    pct.className = 'layer-pct';

    root.append(eye, name, pct);
    return { root, eye, name, pct, target };
  }

  /** Baut die Liste eigener Möbel neu (nach Speichern, Laden oder Löschen). */
  renderCustomFurniture(list: CustomFurnitureData[]): void {
    this.customButtons.clear();
    this.customGrid.textContent = '';
    for (const tpl of list) {
      const item = document.createElement('div');
      item.className = 'furniture-item';
      item.appendChild(this.furnitureButton(tpl));
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'furniture-del';
      del.textContent = '×';
      del.title = `„${tpl.name}“ aus der Bibliothek entfernen (gestellte Exemplare bleiben)`;
      del.onclick = (e) => {
        e.stopPropagation();
        if (confirm(`„${tpl.name}“ aus der Möbel-Bibliothek entfernen?`)) this.cb.onDeleteCustomFurniture(tpl.id);
      };
      item.appendChild(del);
      this.customButtons.set(tpl.id, item.querySelector('.furniture-btn')!);
      this.customGrid.appendChild(item);
    }
    const empty = document.createElement('p');
    empty.className = 'furniture-empty';
    empty.textContent = list.length === 0 ? 'Noch keine — eigene zeichnen, gruppieren und oben speichern.' : '';
    this.customGrid.appendChild(empty);
  }

  private furnitureButton(tpl: FurnitureTemplate | CustomFurnitureData): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'furniture-btn';
    // Flex liegt auf dem inneren Span: Buttons als Flex-Container wachsen in
    // Safari nicht mit umgebrochenem Inhalt (Text läuft über den Rand)
    b.innerHTML = `<span class="furniture-inner"><span class="furniture-name">${tpl.name}</span><span class="furniture-dims">${tpl.note}</span></span>`;
    b.title = `${tpl.name} · ${tpl.note} cm — auswählen, dann in die Szene klicken · ←/→ dreht um 90°`;
    b.onclick = () => {
      this.setActiveFurniture(tpl.id);
      this.cb.onPlaceFurniture(tpl);
    };
    return b;
  }

  private saveSelection(): void {
    const result = this.cb.onSaveSelectionAsFurniture(this.saveInput.value);
    this.showFurnitureMsg(result.message, result.ok);
    if (result.ok) this.saveInput.value = '';
  }

  private showFurnitureMsg(message: string, ok: boolean): void {
    this.furnitureMsg.textContent = message;
    this.furnitureMsg.classList.toggle('error', !ok);
    if (this.msgTimer !== undefined) window.clearTimeout(this.msgTimer);
    if (message) {
      this.msgTimer = window.setTimeout(() => {
        this.furnitureMsg.textContent = '';
      }, 5000);
    }
  }

  private msgTimer: number | undefined;

  syncInputs(state?: VanState): void {
    if (state) this.state = { ...state };
    this.lenInput.value = String(Math.round(this.state.length * 100));
    this.widInput.value = String(Math.round(this.state.width * 100));
    this.heiInput.value = String(Math.round(this.state.height * 100));
    this.sel.value = this.state.templateId;
    this.vanVisCb.checked = this.state.visible;
    this.archCb.checked = this.state.arches;
  }

  setInfo(edges: number, faces: number, guides: number): void {
    this.infoEl.innerHTML =
      `<div>Kanten: <b>${edges}</b></div><div>Flächen: <b>${faces}</b></div><div>Hilfslinien: <b>${guides}</b></div>` +
      `<div class="panel-note">Van: ${templateById(this.state.templateId).name} · ${formatLength(this.state.length, 'm')} × ${formatLength(this.state.width, 'm')} × ${formatLength(this.state.height, 'm')}</div>`;
  }
}

function section(title: string): HTMLElement {
  const box = document.createElement('div');
  box.className = 'panel-section';
  const h = document.createElement('h3');
  h.textContent = title;
  box.appendChild(h);
  return box;
}

function dimInput(): HTMLInputElement {
  const i = document.createElement('input');
  i.type = 'text';
  i.inputMode = 'decimal';
  i.className = 'dim-input';
  return i;
}

function checkboxRow(label: string, checked: boolean, onChange: (v: boolean) => void): HTMLElement {
  const row = document.createElement('label');
  row.className = 'check-row';
  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.checked = checked;
  cb.onchange = () => onChange(cb.checked);
  row.append(cb, document.createTextNode(label));
  return row;
}
